import { useEffect, useRef } from 'react'
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import {
  BADGE_GROUPS,
  BADGE_LOOKS,
  badgeProgressRows,
  badgeRatio,
  badgeStatus,
  badgesInGroup,
  closestBadges,
  isOwnMemberPage,
  knownBadges,
  memberBadgesHref,
  ownStanding,
  shownBadges,
} from 'lib/community/model'
import {
  conversationStarterPending,
  starterTileWaiting,
  type BadgeFacts,
} from 'lib/community/gamification'
import { closestPinned } from 'lib/community/hub'
import type { CommunityMemberResponse } from 'lib/community/types'
import BadgeHex from './BadgeHex'
import {
  BadgePanel,
  BadgePopoverBody,
  useBadgeDisclosure,
  type BadgeSurface,
  type KnownBadge,
} from './BadgePopover'
import { POPOVER_ZONE_ATTR } from './Popover'
import { useOwnMemberMe } from './useOwnMemberMe'
import styles from './Badges.module.css'
import memberStyles from './Member.module.css'

export interface MemberBadgesProps {
  member: CommunityMemberResponse
  /** The summary's caps and badge points (`badgeFacts`) — public page props. */
  facts: BadgeFacts
  /** The summary's Early Owner cap (`foundingCapOf`). */
  foundingCap: number | null
}

/**
 * Who is looking. On the server and the first client render nobody is the member, so the
 * shared-cached HTML is the VISITOR view (earned badges only, Constraint 897af2f1); `/me` makes
 * it the member's own view after hydration (`useOwnMemberMe`, the "This is you" rule). The
 * member's locked badges come from the same public page props — no second read.
 */
export function badgeSurface(
  { member, facts, foundingCap }: MemberBadgesProps,
  me: Parameters<typeof ownStanding>[0],
  trailingSlash: boolean
): BadgeSurface {
  const standing = ownStanding(me, member.handle)
  return {
    member,
    badges: knownBadges(member.badges),
    facts,
    foundingCap,
    own: me !== null && isOwnMemberPage(me, member.handle),
    starterWaiting: conversationStarterPending(standing) !== null,
    pinned: closestPinned(standing),
    trailingSlash,
  }
}

/**
 * Escape hands focus back to the badge. CAPTURE phase on `document`: the disclosure group's own
 * (bubble) Escape listener closes the popover, and that close re-renders synchronously — the ×
 * holding focus unmounts and this effect's cleanup runs mid-dispatch, so a bubble listener
 * registered after the group's never fires and focus falls to <body>. Capture runs first.
 * Returns the cleanup.
 */
export function focusBackOnEscape(
  doc: Pick<Document, 'addEventListener' | 'removeEventListener'>,
  focus: () => void
): () => void {
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      focus()
    }
  }
  doc.addEventListener('keydown', onKey, true)
  return () => doc.removeEventListener('keydown', onKey, true)
}

function useBadgeSurface(props: MemberBadgesProps): BadgeSurface {
  return badgeSurface(props, useOwnMemberMe(props.member.handle), publicConfig('useTrailingSlash'))
}

/** One badge: a button (hexagon, name, its one status) that toggles its popover. `card` is the
 *  Closest-to-unlock layout (hexagon left, a progress bar under the status). */
const BadgeTile: React.VFC<{
  badge: KnownBadge
  surface: BadgeSurface
  open: boolean
  toggle: (id: string) => void
  close: () => void
  variant?: 'tile' | 'card'
}> = ({ badge, surface, open, toggle, close, variant = 'tile' }) => {
  const anchor = useRef<HTMLLIElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const look = BADGE_LOOKS[badge.id]
  const status = badgeStatus(
    badge,
    surface.facts.caps[badge.id] ?? null,
    starterTileWaiting(badge, surface.starterWaiting)
  )
  const popId = `badge-pop-${badge.id}`
  // Escape closes through the group's document listener; focus goes back to this badge.
  useEffect(
    () => (open ? focusBackOnEscape(document, () => trigger.current?.focus()) : undefined),
    [open]
  )
  const dismiss = (): void => {
    close()
    trigger.current?.focus()
  }
  const card = variant === 'card'
  const rows = card ? badgeProgressRows(badge) : null
  return (
    <li
      ref={anchor}
      className={clsx(styles.badge, card && styles.cardItem)}
      {...{ [POPOVER_ZONE_ATTR]: '' }}
    >
      <button
        ref={trigger}
        type="button"
        className={card ? styles.card : styles.tile}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? popId : undefined}
        onClick={() => toggle(badge.id)}
      >
        <BadgeHex
          badge={badge}
          member={surface.member}
          foundingCap={surface.foundingCap}
          className={card ? styles.hexSmall : undefined}
        />
        <span className={card ? styles.cardText : styles.tileText}>
          <span className={styles.name}>{look.name}</span>
          <span className={clsx(styles.status, styles[`status_${status.tone}`])}>
            {status.text}
          </span>
          {rows && (
            <span className={styles.cardBar} aria-hidden="true">
              <span
                className={styles.cardFill}
                style={{ width: `${Math.round(badgeRatio(rows) * 100)}%` }}
              />
            </span>
          )}
        </span>
      </button>
      {open && (
        <BadgePanel id={popId} labelledBy={`${popId}-title`} anchor={anchor} onClose={dismiss}>
          <BadgePopoverBody badge={badge} surface={surface} titleId={`${popId}-title`} />
        </BadgePanel>
      )}
    </li>
  )
}

/**
 * The Overview's badges (design 2c / 2d). A visitor sees the earned badges only — and no
 * section at all when there are none (a block of grey hexagons reads as "did nothing"). The
 * member, after hydration, sees "X of Y", the earned row, the three badges closest to unlock
 * (`closestBadges`) and the way to the Badges page. One popover open across the whole block.
 */
export const MemberBadgesView: React.VFC<{ surface: BadgeSurface }> = ({ surface }) => {
  const { openId, toggle, close } = useBadgeDisclosure()
  const earned = surface.badges.filter(badge => badge.earned)
  const closest = surface.own
    ? closestBadges(surface.badges, { limit: 3, pinned: surface.pinned, caps: surface.facts.caps })
    : []
  if (!surface.own && earned.length === 0) {
    return null
  }
  const tileProps = (badge: KnownBadge): React.ComponentProps<typeof BadgeTile> => ({
    badge,
    surface,
    open: openId === badge.id,
    toggle,
    close,
  })
  return (
    <section className={memberStyles.block} aria-labelledby="badges-title">
      <div className={styles.head}>
        <h2 id="badges-title" className={memberStyles.h2}>
          {'Badges '}
          <span className={memberStyles.h2Count}>
            {surface.own ? `${earned.length} of ${surface.badges.length}` : earned.length}
          </span>
        </h2>
        {surface.own && (
          <Linked
            href={memberBadgesHref(surface.member.handle, surface.trailingSlash)}
            className={styles.allLink}
          >
            {'All badges →'}
          </Linked>
        )}
      </div>
      {earned.length > 0 && (
        <div>
          {surface.own && <p className={styles.eyebrow}>{'Earned'}</p>}
          <ul className={styles.grid}>
            {earned.map(badge => (
              <BadgeTile key={badge.id} {...tileProps(badge)} />
            ))}
          </ul>
        </div>
      )}
      {closest.length > 0 && (
        <div>
          <p className={styles.eyebrow}>{'Closest to unlock'}</p>
          <ul className={styles.cards}>
            {closest.map(({ badge }) => (
              <BadgeTile key={badge.id} {...tileProps(badge)} variant="card" />
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

/**
 * The Badges page (design 2c, "grouped so new ones slot in"): Limited, Reviews, Conversations,
 * Milestones. The member sees every badge with "earned / total" per group; a visitor (and the
 * shared-cached HTML) the earned ones only, empty groups hidden — and one line when nothing is
 * earned. One popover open across the page.
 */
export const MemberBadgeGroupsView: React.VFC<{ surface: BadgeSurface }> = ({ surface }) => {
  const { openId, toggle, close } = useBadgeDisclosure()
  const shown = shownBadges(surface.badges, surface.own)
  const groups = BADGE_GROUPS.map(group => ({
    ...group,
    all: badgesInGroup(surface.badges, group.id),
    shown: badgesInGroup(shown, group.id),
  })).filter(group => group.shown.length > 0)
  if (groups.length === 0) {
    return <p className={styles.empty}>{`${surface.member.handle} hasn’t earned a badge yet.`}</p>
  }
  return (
    <div className={styles.groups}>
      {groups.map(group => (
        <section
          key={group.id}
          className={memberStyles.block}
          aria-labelledby={`badges-${group.id}`}
        >
          <div className={styles.groupHead}>
            <h2 id={`badges-${group.id}`} className={styles.groupTitle}>
              {`${group.label} `}
              <span className={memberStyles.h2Count}>
                {surface.own
                  ? `${group.all.filter(badge => badge.earned).length} / ${group.all.length}`
                  : group.shown.length}
              </span>
            </h2>
            {group.id === 'limited' && (
              <p className={styles.groupNote}>{'Can’t be earned once they’re gone.'}</p>
            )}
          </div>
          <ul className={styles.grid}>
            {group.shown.map(badge => (
              <BadgeTile
                key={badge.id}
                badge={badge}
                surface={surface}
                open={openId === badge.id}
                toggle={toggle}
                close={close}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

/** The Overview block on the member page — the visitor view in the HTML, the member's after
 *  hydration. */
export const MemberBadges: React.VFC<MemberBadgesProps> = props => (
  <MemberBadgesView surface={useBadgeSurface(props)} />
)

/** The Badges page's groups — the visitor view in the HTML, the member's after hydration. */
export const MemberBadgeGroups: React.VFC<MemberBadgesProps> = props => (
  <MemberBadgeGroupsView surface={useBadgeSurface(props)} />
)
